import { classNames } from "helpers/ClassNameHelper";
import * as React from "react";
import { ReactComponent as CheckboxChecked } from "../../../../assets/icons/checkbox-checked.svg";
import { ReactComponent as CheckBoxEmpty } from "../../../../assets/icons/checkbox-empty.svg";
import { ReactComponent as CheckBoxPartial } from "../../../../assets/icons/checkbox-partial.svg";

interface PtClassName {
    container?: string[];
    label?: string[];
    text?: string[];
    checkbox?: string[];
}

interface Props {
    isChecked: boolean;
    isPartial?: boolean;
    isDisabled?: boolean;
    isReversed?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    type: "checkbox" | "radio";
    value?: string;
    text?: React.ReactNode;
    className?: string;
    ptClassName?: PtClassName;
    htmlFor: string;
}

export const Checkbox: React.FC<Props> = ({
    isChecked,
    isPartial,
    isDisabled,
    isReversed,
    onChange,
    type,
    value = "",
    text,
    className,
    ptClassName = {},
    htmlFor,
}) => {
    const defaultPtClassName: PtClassName = { container: [], label: [], text: [], checkbox: [] };
    const classes = { ...defaultPtClassName, ...ptClassName };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLLabelElement>, input: HTMLInputElement) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (isDisabled) return;

            onChange({
                ...({ target: input } as React.ChangeEvent<HTMLInputElement>),
                currentTarget: input,
                target: { ...input, checked: !isChecked },
            } as React.ChangeEvent<HTMLInputElement>);
        }
    };

    const renderCheckboxIcon = () => {
        const commonProps = {
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent<SVGSVGElement>) => {
                const input = document.getElementById(htmlFor) as HTMLInputElement | null;
                if (input && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    if (!isDisabled) {
                        onChange({
                            ...({ target: input } as React.ChangeEvent<HTMLInputElement>),
                            currentTarget: input,
                            target: { ...input, checked: !isChecked },
                        } as React.ChangeEvent<HTMLInputElement>);
                    }
                }
            },
        };

        if (isChecked)
            return (
                <CheckboxChecked
                    {...commonProps}
                    className={classNames("checkbox checked", ...(classes.checkbox || []))}
                />
            );
        if (isPartial)
            return (
                <CheckBoxPartial
                    {...commonProps}
                    className={classNames("checkbox partial", ...(classes.checkbox || []))}
                />
            );
        return (
            <CheckBoxEmpty
                {...commonProps}
                className={classNames("checkbox empty", ...(classes.checkbox || []))}
            />
        );
    };

    const renderContent = () => {
        if (!isReversed && text)
            return (
                <>
                    {text && (
                        <div className={classNames("checkbox-text", ...(classes.text || []))}>
                            {text}
                        </div>
                    )}
                    {renderCheckboxIcon()}
                </>
            );
        if (text)
            return (
                <>
                    {renderCheckboxIcon()}
                    {text && (
                        <div className={classNames("checkbox-text", ...(classes.text || []))}>
                            {text}
                        </div>
                    )}
                </>
            );
        return renderCheckboxIcon();
    };

    return (
        <div className={classNames("checkbox-container", className, ...(classes.container || []))}>
            <label
                className={classNames(
                    "checkbox",
                    isDisabled && "checkbox-disabled",
                    ...(classes.label || [])
                )}
                htmlFor={htmlFor}
            >
                {renderContent()}
                <input
                    type={type}
                    checked={isChecked}
                    onChange={onChange}
                    id={htmlFor}
                    value={value}
                    disabled={isDisabled}
                    style={{ display: "none" }}
                />
            </label>
        </div>
    );
};
